"use client";

import Image from "next/image";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import { SearchableCombobox } from "@/components/ui/SearchableCombobox";
import logoOrange from "../../public/logo-orange.png";

// Past this many studios a plain <Select> is unusable, so the switcher becomes
// a searchable combobox instead.
const WORKSPACE_SEARCH_THRESHOLD = 10;

export function WorkspaceLogo({ workspace }) {
  if (workspace?.image) {
    return (
      <Image
        src={workspace.image}
        alt={`${workspace.name} Logo`}
        width={200}
        height={200}
        className="h-10 w-24 object-contain sm:h-16 sm:w-40"
        priority
      />
    );
  }
  return (
    <div className="flex items-center gap-2">
      <Image
        src={logoOrange}
        alt="Salesably Logo"
        width={32}
        height={32}
        className="h-8 w-8 object-contain"
        priority
      />
      <span className="hidden text-lg font-medium text-gray-800 sm:inline">
        {workspace?.name || "Select Studio"}
      </span>
    </div>
  );
}

export default function WorkspaceSwitcher({
  currentWorkspace,
  workspaces,
  value,
  onSelect,
  isSwitching,
}) {
  if (!workspaces || workspaces.length === 0) return null;

  const studioSwitcherLabel = currentWorkspace?.name
    ? `Switch studio. Current studio: ${currentWorkspace.name}`
    : "Select a studio";

  if (workspaces.length > WORKSPACE_SEARCH_THRESHOLD) {
    return (
      <SearchableCombobox
        items={workspaces}
        selectedValue={value || undefined}
        onSelect={(id) => {
          if (id === currentWorkspace?.id) return;
          const workspace = workspaces.find((w) => w.id === id);
          if (workspace) onSelect(workspace);
        }}
        placeholder="Select a studio..."
        searchPlaceholder="Search studios..."
        emptyMessage="No studios found."
        ariaLabel={studioSwitcherLabel}
        disabled={isSwitching}
        className="bg-transparent border-0 hover:bg-gray-100 rounded p-0 h-auto min-w-0 sm:min-w-[200px] focus:ring-0 focus:ring-offset-0"
        popoverContentClassName="w-[22rem] max-w-[calc(100vw-2rem)]"
        renderSelected={() => <WorkspaceLogo workspace={currentWorkspace} />}
      />
    );
  }

  return (
    <Select
      value={value || undefined}
      disabled={isSwitching}
      onValueChange={(nextValue) => {
        if (nextValue === currentWorkspace?.id) return;
        const workspace = workspaces.find((w) => w.id === nextValue);
        if (workspace) {
          onSelect(workspace);
        }
      }}
    >
      <SelectTrigger
        aria-label={studioSwitcherLabel}
        disabled={isSwitching}
        className="flex items-center gap-2 bg-transparent border-0 hover:bg-gray-100 rounded p-0 h-auto min-w-0 sm:min-w-[200px]"
      >
        <WorkspaceLogo workspace={currentWorkspace} />
      </SelectTrigger>
      <SelectContent>
        {workspaces.map((workspace) => (
          <SelectItem
            key={workspace.id}
            value={workspace.id}
            className="text-base text-gray-700"
          >
            {workspace.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
